/**
 * [INPUT]: 依赖 Tailwind utility class 与 lucide 图标
 * [OUTPUT]: 对外提供后台统一的按钮、输入/文本域、卡片、表格、状态、加载、空态与可滚动响应式弹窗组件
 * [POS]: admin-web 设计系统基础层，模拟 shadcn/ui 的可组合 API，并统一兜底长表单的视口与触控边界
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { AlertCircle, Check, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { cn, titleCaseStatus } from '../lib/utils';

export function Button({ className, variant = 'primary', size = 'md', children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline'; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-moss/40 disabled:pointer-events-none disabled:opacity-45',
        variant === 'primary' && 'bg-ink text-white shadow-sm hover:bg-ink/90',
        variant === 'secondary' && 'bg-mint text-ink hover:bg-mint/80',
        variant === 'ghost' && 'text-slate-600 hover:bg-slate-100 hover:text-ink',
        variant === 'danger' && 'bg-danger text-white hover:bg-danger/90',
        variant === 'outline' && 'border border-line bg-white text-slate-700 hover:border-moss/40 hover:bg-moss/[0.03]',
        size === 'sm' && 'min-h-8 px-3 text-xs',
        size === 'md' && 'min-h-10 px-4 text-sm',
        size === 'lg' && 'min-h-12 px-5 text-sm',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn('h-10 w-full rounded-lg border border-line bg-white px-3 text-sm text-ink outline-none placeholder:text-slate-400 focus:border-moss focus:ring-2 focus:ring-moss/10', className)}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn('min-h-28 w-full resize-y rounded-lg border border-line bg-white px-3 py-2.5 text-sm leading-6 text-ink outline-none placeholder:text-slate-400 focus:border-moss focus:ring-2 focus:ring-moss/10', className)} {...props} />;
}

export function SearchInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={cn('relative w-full max-w-xs', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input className="pl-9" {...props} />
    </div>
  );
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className={cn('relative', className)}>
      <select className="h-10 w-full appearance-none rounded-lg border border-line bg-white px-3 pr-9 text-sm text-slate-700 outline-none focus:border-moss focus:ring-2 focus:ring-moss/10" {...props}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={cn('rounded-2xl border border-line bg-panel shadow-panel', className)}>{children}</section>;
}

export function CardHeader({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
      <div>
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        {description && <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function Badge({ status, children, className }: { status?: string; children?: ReactNode; className?: string }) {
  const semantic = status ?? '';
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide',
      ['published', 'active', 'completed', 'info'].includes(semantic) && 'bg-emerald-50 text-emerald-700',
      ['draft', 'scheduled', 'pending', 'staged', 'streaming'].includes(semantic) && 'bg-amber-50 text-amber-700',
      ['paused', 'blocked', 'failed', 'critical'].includes(semantic) && 'bg-rose-50 text-rose-700',
      ['archived', 'rolled_back', 'warning'].includes(semantic) && 'bg-slate-100 text-slate-600',
      !semantic && 'bg-slate-100 text-slate-600',
      className,
    )}>
      {semantic === 'published' || semantic === 'active' || semantic === 'completed' ? <span className="h-1.5 w-1.5 rounded-full bg-current" /> : null}
      {children ?? titleCaseStatus(semantic)}
    </span>
  );
}

export function StatusBadge({ status }: { status: string }) {
  return <Badge status={status}>{titleCaseStatus(status)}</Badge>;
}

export function LoadingRows({ columns = 5, rows = 4 }: { columns?: number; rows?: number }) {
  return (
    <div className="animate-pulse divide-y divide-line">
      {Array.from({ length: rows }).map((_, row) => (
        <div className="grid gap-4 px-5 py-4" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }} key={row}>
          {Array.from({ length: columns }).map((__, column) => <div className="h-4 rounded bg-slate-100" key={column} />)}
        </div>
      ))}
    </div>
  );
}

export function LoadingBlock({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-xl bg-slate-100', className)} />;
}

export function ErrorState({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-50 text-rose-600"><AlertCircle className="h-5 w-5" /></div>
      <div><p className="text-sm font-semibold text-ink">数据加载失败</p><p className="mt-1 max-w-sm text-xs leading-5 text-slate-500">{message ?? '接口暂时不可用，请稍后重试。'}</p></div>
      {onRetry && <Button variant="outline" size="sm" onClick={onRetry}>重新加载</Button>}
    </div>
  );
}

export function EmptyState({ title = '还没有数据', description = '当有新的记录时，它们会出现在这里。', action }: { title?: string; description?: string; action?: ReactNode }) {
  return <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center"><div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400"><Check className="h-5 w-5" /></div><p className="text-sm font-semibold text-ink">{title}</p><p className="max-w-sm text-xs leading-5 text-slate-500">{description}</p>{action}</div>;
}

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('w-full overflow-x-auto', className)}><table className="w-full min-w-[680px] border-collapse text-left text-sm">{children}</table></div>;
}

export function TableHead({ children }: { children: ReactNode }) {
  return <thead className="border-b border-line bg-slate-50/80 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{children}</thead>;
}

export function TableRow({ children, className, onClick }: { children: ReactNode; className?: string; onClick?: () => void }) {
  return <tr className={cn('border-b border-line/80 transition last:border-0 hover:bg-moss/[0.025]', onClick && 'cursor-pointer', className)} onClick={onClick}>{children}</tr>;
}

export function TableCell({ children, className, header = false }: { children: ReactNode; className?: string; header?: boolean }) {
  const Tag = header ? 'th' : 'td';
  return <Tag className={cn(header ? 'px-5 py-3 font-medium' : 'px-5 py-4 align-middle text-slate-700', className)}>{children}</Tag>;
}

export function Kicker({ children }: { children: ReactNode }) {
  return <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-moss">{children}</span>;
}

export function Modal({ open, title, description, onClose, children, width = 'max-w-md' }: { open: boolean; title: string; description?: string; onClose: () => void; children: ReactNode; width?: string }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 backdrop-blur-sm sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-label={title}>
      <div className={cn('flex max-h-[calc(100dvh-0.5rem)] w-full flex-col overflow-hidden rounded-t-2xl border border-line bg-white shadow-2xl sm:max-h-[calc(100dvh-3rem)] sm:rounded-2xl', width)}>
        <div className="flex shrink-0 items-start justify-between border-b border-line px-5 py-4"><div><h2 className="text-base font-semibold text-ink">{title}</h2>{description && <p className="mt-1 text-xs text-slate-500">{description}</p>}</div><button className="-mr-2 -mt-2 flex min-h-11 min-w-11 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-ink" onClick={onClose} aria-label="关闭"><X className="h-4 w-4" /></button></div>
        <div className="min-h-0 overflow-y-auto overscroll-contain p-5">{children}</div>
      </div>
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('h-4 w-4 animate-spin', className)} />;
}
